/*
 * -----------------------------------------------------------------------
 *  <copyright file="import-crfs.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tabs } from '@mui/material';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import React, { useState } from 'react';
import { useGridCells, useTranslation } from 'hooks';
import ExpandableGrid from 'views/components/core/expandable/grid';

const TabContainer = ({ children }: any) => (
    <Typography component="div" style={{ padding: 24 }}>
        {children}
    </Typography>
);

export const ImportCrfs = ({ data }: any) => {
    const { t } = useTranslation();
    const { YesNoCell } = useGridCells();

    if (!data || !data.length) return <div>{t('admin:importCRFNoData')}</div>;

    return (
        <>
            <div className="secondary" style={{ fontSize: 14, marginBottom: 20 }}>
                {t('admin:confirmImportedCrfs', { count: data.length })}
            </div>
            <ExpandableGrid
                customKey="imported-crfs-grid"
                id="imported-crfs"
                key="imported-crfs-grid"
                items={data}
                groupable={false}
                showPager
                scrollable="scrollable"
                reorderable
                pageable={false}
                resizable
                removeHideFilter
                options={{
                    allowExcelExport: false,
                    allowExpandCollapseAll: false,
                    useFilterChips: false,
                }}
                columns={[
                    {
                        field: 'active',
                        label: t('admin:active'),
                        width: 150,
                        cells: { data: YesNoCell },
                    },
                    {
                        field: 'required',
                        label: t('ra:required'),
                        width: 150,
                        cells: { data: YesNoCell },
                    },
                    {
                        field: 'subjectCode',
                        label: t('admin:sourceSystemId'),
                        width: 200,
                    },
                    {
                        field: 'crfName',
                        label: t('name'),
                        width: 200,
                    },
                ]}
                showFirstColumn={false}
            />
        </>
    );
};

export const ImportCrfsInstructions = () => {
    const { t } = useTranslation();
    const [value, setValue] = useState(0);

    return (
        <>
            <div className="secondary" style={{ fontSize: 14, marginBottom: 15 }}>
                {t('admin:importCrfsInstructions')}
            </div>
            <Grid
                style={{ fontSize: 11 }}
                data={[
                    {
                        number: 1,
                        field: t('admin:active'),
                        type: 'Y/N',
                        required: 'No',
                        notes: 'y | n (case-insensitive)',
                    },
                    {
                        number: 2,
                        field: t('ra:required'),
                        type: 'Y/N',
                        required: 'No',
                        notes: 'y | n (case-insensitive)',
                    },
                    { number: 3, field: t('admin:sourceSystemId'), required: 'Yes', type: 'Text', maxLength: 30 },
                    { number: 4, field: t('name'), required: 'Yes', type: 'Text', maxLength: 60 },
                ]}
            >
                <Column title={t('ra:columnNum')} field="number" width={120} />
                <Column title={t('ra:field')} field="field" width={120} />
                <Column title={t('ra:required')} field="required" width={100} />
                <Column title={t('ra:maxLength')} field="maxLength" width={120} />
                <Column title={t('ra:values')} field="notes" />
            </Grid>
            <h3>{t('ra:Examples')}:</h3>
            <Tabs
                value={value}
                onChange={(e, newValue) => setValue(newValue)}
                indicatorColor="primary"
                textColor="primary"
                centered
            >
                <Tab label="XLSX" icon={<FontAwesomeIcon icon={['fal', 'file-excel']} />} data-testid="xlsx-tab" />
                <Tab label="CSV" icon={<FontAwesomeIcon icon={['fal', 'file-csv']} />} data-testid="csv-tab" />
                <Tab label="PSV" icon={<FontAwesomeIcon icon={['fal', 'file-spreadsheet']} />} data-testid="psv-tab" />
            </Tabs>
            {value === 0 && (
                <TabContainer>
                    <img src="/img/import-crfs-example-xlsx.png" alt="XLSX" className="import-image-example" />
                </TabContainer>
            )}
            {value === 1 && (
                <TabContainer>
                    <img src="/img/import-crfs-example-csv.png" alt="CSV" className="import-image-example" />
                </TabContainer>
            )}
            {value === 2 && (
                <TabContainer>
                    <img src="/img/import-crfs-example-psv.png" alt="PSV" className="import-image-example" />
                </TabContainer>
            )}
        </>
    );
};
